import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';

import { STORAGE_KEYS } from '@/constants/storageKeys';

/**
 * Analytics Event Constants
 *
 * **Naming Convention:**
 * We use a consistent naming pattern for cross-platform compatibility:
 * - **Feature prefix**: Start with the feature name (e.g., ATTACHMENTS_, PROFILE_, INQUIRY_)
 * - **Past tense action**: Use past tense verbs (e.g., TRIGGERED, REMOVED, CREATED, VIEWED)
 * - **Platform agnostic**: Avoid platform-specific terms like "clicked" vs "tapped"
 * - **Generic actions**: Focus on what happened, not how it happened
 *
 * Examples:
 * - ATTACHMENTS_TRIGGERED (not "attach button clicked")
 * - PROFILE_VIEWED (not "profile screen opened")
 * - INQUIRY_SUBMITTED (not "form submitted")
 *
 * **Auth Event Policy:**
 * - Generic event names with method/provider properties (provider-agnostic)
 * - LOG_IN, LOGIN_FAILED, LOG_OUT with method/provider context
 * - CREATE_ACCOUNT_STEP_1_COMPLETED, ACCOUNT_CREATED for both password/OAuth flows
 * - NPI_VERIFICATION_COMPLETED/FAILED for account completion
 */
export const ANALYTICS_EVENTS = {
  ACCOUNT_AWAY_PERIOD_REMOVED: 'Away period removed',
  ACCOUNT_AWAY_PERIOD_SAVED: 'Away period saved',
  ACCOUNT_AWAY_PERIOD_SAVE_FAILED: 'Away period save failed',
  ACCOUNT_AWAY_SHEET_VIEWED: 'Away sheet viewed',
  ACCOUNT_AWAY_TUTORIAL_UPDATED: 'Away tutorial updated',
  ACCOUNT_CONFLICT_DETECTED: 'Account conflict detected',
  ACCOUNT_CREATED: 'Account created',
  ACCOUNT_CREATED_UNVERIFIED: 'Account created npi unverified',
  ACCOUNT_CREATE_ERRORED: 'Account create errored',
  ACCOUNT_LINKED: 'Account linked',
  ACCOUNT_LINKING_FAILED: 'Account linking failed',
  ACCOUNT_LINKING_INITIATED: 'Account linking initiated',
  ACCOUNT_MENU_CLICKED: 'Account menu clicked',
  ADD_REFERENCES_FAILED: 'Add references failed',
  ADD_REFERENCES_PROMPT_VIEWED: 'Add references prompt viewed',
  ADD_REFERENCES_SHEET_CANCELED: 'Add references sheet canceled',
  ADD_REFERENCES_SHEET_OPENED: 'Add references sheet opened',
  ADD_REFERENCES_SUBMITTED: 'Add references submitted',
  ADD_REFERENCES_SUCCEEDED: 'Add references succeeded',
  ADDITIONAL_EXPERTS_ENABLED: 'Additional experts enabled',
  ADDITIONAL_EXPERTS_SELECTED: 'Additional experts selected',
  ANNOUNCEMENT_CLICKED: 'Announcement clicked',
  ANNOUNCEMENT_LINK_NAVIGATED: 'Announcement link navigated',
  ANONYMOUS_INQUIRY_TOGGLED: 'Anonymous inquiry toggled',
  EXTRACTED_QUESTIONS_RENDERED: 'Extracted questions rendered',
  EXTRACTED_STATEMENTS_RENDERED: 'Extracted statements rendered',
  ON_YOUR_HONOR_TRIGGERED: 'On your honor triggered',
  ON_YOUR_HONOR_CONFIRMED: 'On your honor confirmed',
  ON_YOUR_HONOR_REMOVED: 'On your honor removed',
  ON_YOUR_HONOR_CANCELLED: 'On your honor cancelled',
  ON_YOUR_HONOR_SUBMITTED: 'On your honor submitted',
  ON_YOUR_HONOR_REASON_VIEWED: 'On your honor reason viewed',
  ON_YOUR_HONOR_REASON_SUBMITTED: 'On your honor reason submitted',
  ON_YOUR_HONOR_REASON_DISMISSED: 'On your honor reason dismissed',
  ON_YOUR_HONOR_INTRO_VIEWED: 'On your honor intro viewed',
  ON_YOUR_HONOR_INTRO_DISMISSED: 'On your honor intro dismissed',
  REFERENCE_ASSISTANT_INTRO_VIEWED: 'Reference assistant intro viewed',
  REFERENCE_ASSISTANT_INTRO_DISMISSED: 'Reference assistant intro dismissed',
  APP_INITIALIZED: 'App initialized',
  ASK_AN_EXPERT_FORM_LOADED: 'Ask an Expert form loaded',
  ASK_AN_EXPERT_FORM_SUBMITTED: 'Ask an Expert form submitted',
  ATTACHMENT_DOWNLOAD_COMPLETED: 'Attachment download completed',
  ATTACHMENT_DOWNLOAD_FAILED: 'Attachment download failed',
  ATTACHMENT_DOWNLOAD_STARTED: 'Attachment download started',
  ATTACHMENTS_REMOVED: 'Attachments removed',
  ATTACHMENTS_TRIGGERED: 'Attachments triggered',
  AUTH_TRIGGERED: 'Auth triggered',
  BIO_MESSAGE_SELECTED: 'Bio message selected',
  CANCER_SELECTED: 'Cancer selected',
  CANCER_TYPE_SEARCHED: 'Cancer type searched',
  CANCER_TYPE_VIEWED: 'Cancer type viewed',
  CHAT_SESSION_RECOVERED: 'Chat session recovered',
  CHAT_TRANSPORT_SHADOW_COMPARED: 'Chat transport shadow compared',
  CLEARED_SESSION_FOR_UNUSABLE_ACCOUNT: 'Cleared session for unusable account',
  CLOSED_INBOX_VIEWED: 'Closed inbox viewed',
  COMMENT_SUBMITTED: 'Comment submitted',
  COMMENT_CREATED: 'Comment created',
  COMMENT_CREATED_FAILED: 'Comment created failed',
  COMMENT_LIKED: 'Comment liked',
  COMMENT_LIKED_FAILED: 'Comment liked failed',
  COMMENT_UNLIKED: 'Comment unliked',
  COMMENT_UNLIKED_FAILED: 'Comment unliked failed',
  COMMUNITY_CANCER_TYPE_SELECTED: 'Community cancer type selected',
  COMMUNITY_CANCER_TYPES_VIEWED: 'Community cancer types viewed',
  COMMUNITY_CLICKED: 'Community clicked',
  COMMUNITY_DATA_REQUESTED: 'Community data requested',
  COMMUNITY_POST_CTA_CLICKED: 'Community post cta clicked',
  COMMUNITY_POST_LIKED: 'Community post liked',
  COMMUNITY_POST_NEXT_CLICKED: 'Community post next clicked',
  COMMUNITY_POST_PREV_CLICKED: 'Community post prev clicked',
  COMMUNITY_POST_READ: 'Community post read',
  COMMUNITY_POST_SAVED: 'Community post saved',
  COMMUNITY_POST_SHARE_CANCELLED: 'Community post share cancelled',
  COMMUNITY_POST_SHARE_COMPLETED: 'Community post share completed',
  COMMUNITY_POST_SHARE_STARTED: 'Community post share started',
  COMMUNITY_POST_UNLIKED: 'Community post unliked',
  COMMUNITY_POST_UNSAVED: 'Community post unsaved',
  COMMUNITY_POST_VIEWED: 'Community post viewed',
  COMMUNITY_SEARCH_RESULT_OPENED: 'Community search result opened',
  COMMUNITY_SHARED_POST_REDIRECTED: 'Community shared post redirected',
  COMMUNITY_SORT_BY: 'Community sort by',
  COMMUNITY_SORT_BY_FAILED: 'Community sort by failed',
  COMMUNITY_TOTAL_POSTS_VIEWED: 'Community total posts viewed',
  COMMUNITY_VIEWED: 'Community viewed',
  CONFIRM_ACCOUNT_DETAILS_ADDRESS_SUGGESTION_SELECTED:
    'Confirm account details address suggestion selected',
  CONFIRM_ACCOUNT_DETAILS_PAGE_SUBMITTED: 'Confirm account details page submitted',
  CONFIRM_ACCOUNT_DETAILS_PAGE_SUBMITTED_UPDATED_MAILING_ADDRESS:
    'Confirm Account Details Page Submitted - Updated Mailing Address for Existing User',
  CONFIRM_ACCOUNT_DETAILS_PAGE_VIEWED: 'Confirm account details page viewed',
  CONTENT_LIBRARY_GATE_VIEWED: 'Content Library gate viewed',
  CONTENT_LIBRARY_ITEM_CLICKED: 'Content Library item clicked',
  CONTENT_LIBRARY_ITEM_LIKE_STATUS_CHANGED: 'Content Library item like status changed',
  CONTENT_LIBRARY_ITEM_VIEW_DOWNLOAD_ARTICLE_LINK_CLICKED:
    'Content Library item View/Download Article link clicked',
  CONTENT_LIBRARY_ITEM_VIEWED: 'Content Library item viewed',
  CONTENT_LIBRARY_LINK_CLICKED: 'Content Library link clicked',
  CONTENT_LIBRARY_ONCOLOGIST_CONFIRMED: 'Content Library oncologist confirmed',
  CONTENT_LIBRARY_ONCOLOGIST_DECLINED: 'Content Library oncologist declined',
  CONTENT_LIBRARY_SHARED_ITEM_REDIRECTED: 'Content Library shared item redirected',
  CONTENT_LIBRARY_TAB_SELECTED: 'Content Library tab selected',
  CONTENT_LIBRARY_VIDEO_CLICKED: 'Content Library video clicked',
  CONTENT_LIBRARY_VIEWED: 'Content Library viewed',
  CONTENT_SUGGESTION_CLICKED: 'Content suggestion clicked',
  CONTENT_SUGGESTION_VIDEO_PLAYED: 'Content suggestion video played',
  CONTENT_SUGGESTION_VIEWED: 'Content suggestion viewed',
  CREATE_ACCOUNT_CLOSED: 'Create account closed',
  CREATE_ACCOUNT_CTA_TRIGGERED: 'Create account cta triggered',
  CREATE_ACCOUNT_REFERRED_BY_BANNER_VIEWED: 'Create account referred by banner viewed',
  CREATE_ACCOUNT_STEP_1_COMPLETED: 'Create account step 1 completed',
  CREATE_ACCOUNT_STEP_2_BACK_TRIGGERED: 'Create account step 2 back triggered',
  CREATE_ACCOUNT_STEP_2_COMPLETED: 'Create account step 2 completed',
  DEEP_LINK_OPENED: 'Deep link opened',
  DELETE_DRAFT_CANCELLED: 'Delete draft cancelled',
  DELETE_DRAFT_FAILED: 'Delete draft failed',
  DELETE_DRAFT_STARTED: 'Delete draft started',
  DIRECTORY_FEATURED_TAB_SELECTED: 'Directory featured tab selected',
  DIRECTORY_MESSAGE_SELECTED: 'Directory message selected',
  DIRECTORY_NAV_ITEM_CLICKED: 'Directory nav item clicked',
  DRAFT_DELETED: 'Draft deleted',
  DRAFTS_VIEWED: 'Drafts viewed',
  EMAIL_SIGNIN_LINK_FORM_SUBMITTED: 'Email a signin link form submitted',
  EMAIL_SIGNIN_LINK_VIEWED: 'Email a signin link viewed',
  /**
   * Tracks when a user views an A/B experiment variant.
   * Use experiment names from EXPERIMENTS constant (see constants/experiments.ts).
   *
   * Properties vary by experiment. For EXPERIMENTS.SURGEON_GROUPING:
   * - experimentName: ExperimentName
   * - variant: 'normal' | 'grouped' | 'grouped_with_mdr'
   * - isApplicable: boolean (whether experiment applies to this cancer type)
   * - surgicalOncologistsGrouped: boolean (whether surgical oncologists are displayed grouped)
   * - multiDisciplinarySuggestionDisplayed: boolean (whether MDR suggestion text was shown)
   * - cancerType: string
   * - surgicalOncologistCount: number
   * - totalExpertCount: number
   * - userName: string
   *
   * For EXPERIMENTS.AVENTRIA_LANDING_PAGE:
   * - experiment: ExperimentName
   * - variant: string (the landing page variant)
   */
  EXPERIMENT_VIEWED: 'Experiment viewed',
  EXPERT_DESELECTED: 'Expert deselected',
  EXPERT_LINK_COPIED: 'Expert link copied',
  EXPERT_LINK_OPENED: 'Expert link opened',
  EXPERT_LINK_SHARED: 'Expert link shared',
  EXPERT_LINK_SHORTCUT_USED: 'Expert link shortcut used',
  EXPERT_MESSAGE_CUSTOMIZED: 'Expert message customized',
  EXPERT_PROFILE_VIEWED_FROM_SELECTION: 'Expert profile viewed from selection',
  EXPERT_REPLY_ATTRIBUTION_CHOSEN: 'Expert reply attribution chosen',
  EXPERT_REMOVED: 'Expert removed',
  /**
   * Tracks when a user selects an expert in the multi-select dropdown.
   *
   * Properties for EXPERIMENTS.SURGEON_GROUPING context:
   * - expertId: number
   * - expertName: string
   * - expertSpeciality: string
   * - selectedCount: number
   * - addedCount: number
   * - userName: string
   * - surgeonGroupingVariant: 'normal' | 'grouped' | 'grouped_with_mdr' | 'not_applicable'
   * - isSurgeon: boolean
   * - cancerType: string
   */
  EXPERT_SELECTED: 'Expert selected',
  EXPERT_SELECTION_MODAL_OPENED: 'Expert selection modal opened',
  EXPERT_SELECTION_TOGGLED: 'Expert selection toggled',
  EXPERTS_CLEARED: 'Experts cleared',
  FEEDBACK_SUCCESS_INVITE_A_PEER_LINK_CLICKED: 'Feedback success Invite a Peer link clicked',
  FELLOWS_BANNER_CLICKED: 'Fellows banner clicked',
  FELLOWS_BANNER_DISMISSED: 'Fellows banner dismissed',
  FELLOWS_BECOME_MENTOR_CLICKED: 'Fellows become mentor clicked',
  FELLOWS_DETAIL_TAB_SELECTED: 'Fellows detail tab selected',
  FELLOWS_GUIDELINES_VIEWED: 'Fellows guidelines viewed',
  FELLOWS_MENTOR_FORM_STARTED: 'Fellows mentor form started',
  FELLOWS_MENTOR_FORM_SUBMITTED: 'Fellows mentor form submitted',
  FELLOWS_MENTOR_FORM_SUBMIT_FAILED: 'Fellows mentor form submit failed',
  FELLOWS_MENTOR_SELECTED: 'Fellows mentor selected',
  FELLOWS_NAV_ITEM_CLICKED: 'Fellows nav item clicked',
  FELLOWS_QUESTION_SELECTED: 'Fellows question selected',
  FELLOWS_QUESTION_TOGGLED: 'Fellows question toggled',
  FILE_UPLOAD_COMPLETED: 'File upload completed',
  FILE_UPLOAD_FAILED: 'File upload failed',
  FORMAT_RESPONSE_APPENDED: 'Format response appended',
  FORMAT_RESPONSE_COMPARISON_VIEWED: 'Format response comparison viewed',
  FORMAT_RESPONSE_COMPLETED: 'Format response completed',
  FORMAT_RESPONSE_COPIED: 'Format response copied',
  FORMAT_RESPONSE_FAILED: 'Format response failed',
  FORMAT_RESPONSE_RETRY_CLICKED: 'Format response retry clicked',
  FORMAT_RESPONSE_TRIGGERED: 'Format response triggered',
  INBOX_ANNOUNCEMENT_BANNER_DISMISSED: 'Inbox announcement banner dismissed',
  INBOX_ANNOUNCEMENT_BANNER_VIEWED: 'Inbox announcement banner viewed',
  INBOX_VIEWED: 'Inbox viewed',
  INQUIRY_CANCEL_CLOSE: 'Inquiry cancel close',
  INQUIRY_CLOSED: 'Inquiry closed',
  INQUIRY_CONVERSATION_DRAFT_PRELOADED: 'Inquiry conversation draft preloaded',
  INQUIRY_CONVERSATION_DRAFT_RESTORED: 'Inquiry conversation draft restored',
  INQUIRY_CONVERSATION_LABEL_ADDED: 'Inquiry conversation label added',
  INQUIRY_CONVERSATION_LABEL_REMOVED: 'Inquiry conversation label removed',
  INQUIRY_DEMO_CLOSED: 'Inquiry demo closed',
  INQUIRY_DEMO_DISMISSED: 'Inquiry demo dismissed',
  INQUIRY_DEMO_OPENED: 'Inquiry demo opened',
  INQUIRY_GROUP_OPENED: 'Inquiry group opened',
  INQUIRY_GROUP_SWITCHED: 'Inquiry group switched',
  INQUIRY_FAILED_SUBMIT: 'Inquiry failed submit',
  INQUIRY_FEEDBACK_DISMISSED: 'Inquiry feedback dismissed',
  INQUIRY_FEEDBACK_FAILED: 'Inquiry feedback failed',
  INQUIRY_FEEDBACK_SUBMITTED: 'Inquiry feedback submitted',
  INQUIRY_GUIDELINES_OPENED: 'Inquiry guidelines opened',
  INQUIRY_GUIDELINES_TAB_SWITCHED: 'Inquiry guidelines tab switched',
  INQUIRY_LIBRARY_ANSWER_COLLAPSED: 'Inquiry library answer collapsed',
  INQUIRY_LIBRARY_ANSWERS_TOGGLED: 'Inquiry library answers toggled',
  INQUIRY_LIBRARY_ANSWERS_VIEWED: 'Inquiry library answers viewed',
  INQUIRY_LIBRARY_REFERENCE_CLICKED: 'Inquiry library reference clicked',
  INQUIRY_LIBRARY_REFERENCES_EXPANDED: 'Inquiry library references expanded',
  INQUIRY_REQUEST_CLOSE: 'Inquiry request close',
  INQUIRY_SUBMITTED: 'Inquiry submitted',
  CLINICAL_CASE_RATED: 'Clinical case rated',
  INQUIRY_SUMMARY_FOLLOW_UP_OPENED: 'Inquiry summary follow-up opened',
  INQUIRY_SUMMARY_FOLLOW_UP_SUBMITTED: 'Inquiry summary follow-up submitted',
  INQUIRY_SUMMARY_RATED: 'Inquiry summary rated',
  INQUIRY_SUMMARY_TOGGLED: 'Inquiry summary toggled',
  INQUIRY_SUMMARY_VIEWED: 'Inquiry summary viewed',
  LIBRARY_CHAT_ASK_EXPERT_TRIGGERED: 'Library chat ask expert triggered',
  LIBRARY_CHAT_CONVERSATION_DELETED: 'Library chat conversation deleted',
  LIBRARY_CHAT_CONVERSATION_OPENED: 'Library chat conversation opened',
  LIBRARY_CHAT_CONVERSATION_RENAMED: 'Library chat conversation renamed',
  LIBRARY_CHAT_ENTRY_TRIGGERED: 'Library chat entry triggered',
  LIBRARY_CHAT_INQUIRY_SUBMITTED: 'Library chat inquiry submitted',
  LIBRARY_CHAT_RESULT_VIEWED: 'Library chat result viewed',
  LIBRARY_CHAT_TURN_COMPLETED: 'Library chat turn completed',
  LIBRARY_CHAT_TURN_FAILED: 'Library chat turn failed',
  LIBRARY_CHAT_TURN_SUBMITTED: 'Library chat turn submitted',
  RESEARCH_TUTORIAL_COMPLETED: 'Research tutorial completed',
  RESEARCH_TUTORIAL_SKIPPED: 'Research tutorial skipped',
  RESEARCH_TUTORIAL_STARTED: 'Research tutorial started',
  LINKED_INQUIRY_BANNER_TRIGGERED: 'Linked inquiry banner triggered',
  LINK_TO_INQUIRY_TRIGGERED: 'Link to inquiry triggered',
  INVITE_A_PEER_CLICKED: 'Invite a peer clicked',
  INVITE_FAILED: 'Invite failed',
  INVITE_LINK_COPIED: 'Invite link copied',
  INVITE_LINK_VISITED: 'Invite link visited',
  INVITE_NATIVE_SHARED: 'Invite native shared',
  INVITE_SCREEN_DISMISSED: 'Invite screen dismissed',
  INVITE_SCREEN_VIEWED: 'Invite screen viewed',
  INVITE_SUBMITTED: 'Invite submitted',
  LOG_IN: 'Log in',
  LOG_OUT: 'Log out',
  LOGIN_FAILED: 'Login failed',
  MENU_SETTINGS_CLICKED: 'Menu Settings clicked',
  MESSAGE_PROCESSING_SURFACED: 'Message processing surfaced',
  /**
   * Staff relayed one message between the two sides of an Ask Anonymously case.
   *
   * Properties:
   * - message_sid, target_conversation_id: string
   * - audience: 'submitter' | 'expert'
   * - status: 'relayed' | 'already_relayed'
   */
  MESSAGE_RELAYED: 'Message relayed',
  MESSAGE_RELAY_FAILED: 'Message relay failed',
  MESSAGE_SEND_FAILED: 'Message send failed',
  MESSAGE_SENT: 'Message sent',
  MESSAGE_TEMPLATE_OPENED: 'Message template opened',
  MESSAGE_TEMPLATE_SELECTED: 'Message template selected',
  MOBILE_NUMBER_SETUP_DISMISSED: 'Mobile number setup dismissed',
  MOBILE_NUMBER_SETUP_FAILED: 'Mobile number setup failed',
  MOBILE_NUMBER_SETUP_VIEWED: 'Mobile number setup viewed',
  MOD_FORM_STARTED: 'MOD form started',
  MOD_FORM_SUBMIT_FAILED: 'MOD form submit failed',
  MOD_FORM_SUBMITTED: 'MOD form submitted',
  MOD_PRODUCT_SUPPORT_EVALUATED: 'MOD product support evaluated',
  MOD_PRODUCT_SUPPORT_SUCCESS_CTA_CLICKED: 'MOD product support success cta clicked',
  MOD_PRODUCT_SUPPORT_SUCCESS_VIEWED: 'MOD product support success viewed',
  MOD_PRODUCT_SUPPORT_SUCCESS_INBOX_CLICKED: 'MOD product support success inbox clicked',
  MOD_GUIDELINES_VIEWED: 'MOD guidelines viewed',
  MOD_INQUIRY_FEEDBACK_DISMISSED: 'Medical on Demand - Inquiry feedback dismissed',
  MOD_INQUIRY_FEEDBACK_FAILED: 'Medical on Demand - Inquiry feedback failed',
  MOD_INQUIRY_FEEDBACK_SUBMITTED: 'Medical on Demand - Inquiry feedback submitted',
  MOD_PRODUCT_SUPPORT_SUCCESS_PRODUCT_CHANGED: 'MOD product support success product changed',
  MOD_PRODUCT_INFO_VIEWED: 'MOD product info viewed',
  MOD_PRODUCT_SELECTED: 'MOD product selected',
  MOD_QUESTION_TYPE_SELECTED: 'MOD question type selected',
  MOD_ROUTE_TOGGLED: 'MOD route toggled',
  MOD_SUBMISSION_SUCCEEDED: 'MOD submission succeeded',
  MOD_WHEN_TO_ASK_CTA_CLICKED: 'MOD when to ask cta clicked',
  MOD_WHEN_TO_ASK_DISMISSED: 'MOD when to ask dismissed',
  MOD_WHEN_TO_ASK_OPENED: 'MOD when to ask opened',
  NOTIFICATION_OPENED: 'Notification opened',
  NOTIFICATIONS_PREFERENCES_UPDATE_FAILED: 'Notification preferences update failed',
  NOTIFICATIONS_PREFERENCES_UPDATED: 'Notifications Preferences Updated',
  NPI_SUGGESTIONS_VIEWED: 'NPI suggestions viewed',
  NPI_SUGGESTION_ACCEPTED: 'NPI suggestion accepted',
  NPI_SUGGESTIONS_REJECTED: 'NPI suggestions rejected',
  NPI_SUGGESTIONS_SEARCHED: 'NPI suggestions searched',
  NPI_REVIEW_PENDING_VIEWED: 'NPI review pending viewed',
  NPI_VALIDATED: 'NPI validated',
  NPI_REGISTRY_CLICKED: 'NPI registry clicked',
  NPI_VALIDATION_FAILED: 'NPI validation failed',
  NPI_VERIFICATION_COMPLETED: 'NPI verification completed',
  NPI_VERIFICATION_FAILED: 'NPI verification failed',
  NPI_VERIFICATION_SKIP_CLICKED: 'NPI verification skip clicked',
  NPI_VERIFICATION_SKIP_CONFIRMED: 'NPI verification skip confirmed',
  NPI_VERIFICATION_SKIP_CANCELLED: 'NPI verification skip cancelled',
  NPI_VERIFICATION_VIEWED: 'NPI verification viewed',
  ONBOARDING_TUTORIAL_BACK: 'Onboarding tutorial back',
  ONBOARDING_TUTORIAL_CLOSED: 'Onboarding tutorial closed',
  ONBOARDING_TUTORIAL_COMPLETED: 'Onboarding tutorial completed',
  ONBOARDING_TUTORIAL_OPENED: 'Onboarding tutorial opened',
  ONBOARDING_TUTORIAL_STEP_VIEWED: 'Onboarding tutorial step viewed',
  OS_NOTIFICATION_PERMISSION_SET: 'OS notification permission set',
  PASSWORD_CHANGE_FAILED: 'Password change failed',
  PASSWORD_CHANGED: 'Password changed',
  PASSWORD_FORGOT: 'Password forgot',
  POLL_HISTORY_VIEWED: 'Poll history viewed',
  POLL_RESULTS_VIEWED: 'Poll results viewed',
  PRIVACY_CLICKED: 'Privacy clicked',
  PRODUCT_TYPE_SELECTED: 'Product type selected',
  PRODUCT_TYPE_VIEWED: 'Product type viewed',
  PROFILE_CLINICAL_TRIAL_OPENED: 'Profile clinical trial opened',
  PROFILE_CLINICAL_TRIALS_SELECTED: 'Profile clinical trials selected',
  PROFILE_CLINICAL_TRIALS_SHOW_LESS: 'Profile clinical trials show less',
  PROFILE_CLINICAL_TRIALS_SHOW_MORE: 'Profile clinical trials show more',
  PROFILE_EDIT_CANCELLED: 'Profile edit cancelled',
  PROFILE_EDIT_FAILED: 'Profile edit failed',
  PROFILE_EDIT_SAVED: 'Profile edit saved',
  PROFILE_EDIT_STARTED: 'Profile edit started',
  PROFILE_PUBLICATION_OPENED: 'Profile publication opened',
  PROFILE_PUBLICATIONS_SELECTED: 'Profile publications selected',
  PROFILE_PUBLICATIONS_SHOW_LESS: 'Profile publications show less',
  PROFILE_PUBLICATIONS_SHOW_MORE: 'Profile publications show more',
  PROFILE_SIGN_IN_REQUIRED: 'Profile sign in required',
  PROFILE_VIEWED: 'Profile viewed',
  REFERENCE_BADGE_OPENED: 'Reference badge opened',
  REFERENCE_DETAILS_VIEWED: 'Reference details viewed',
  REFERENCE_VIEWED: 'Reference viewed',
  REFERENCE_WIZARD_NOTIFY_REQUESTED: 'Reference wizard notify requested',
  REPLY_DRAFT_CLOSED: 'Reply draft closed',
  REPLY_DRAFT_CONFLICTED: 'Reply draft conflicted',
  REPLY_DRAFT_OPENED: 'Reply draft opened',
  REPLY_DRAFT_REPLACE_DECLINED: 'Reply draft replace declined',
  REPLY_DRAFT_SOURCE_VIEWED: 'Reply draft source viewed',
  REPLY_DRAFT_STEP_VIEWED: 'Reply draft step viewed',
  REPLY_DRAFT_TOUR_COMPLETED: 'Reply draft tour completed',
  REPLY_DRAFT_TOUR_SKIPPED: 'Reply draft tour skipped',
  REPLY_DRAFT_TOUR_STARTED: 'Reply draft tour started',
  REPLY_DRAFT_USE_FAILED: 'Reply draft use failed',
  REFERENCES_READY_DEEP_LINK_OPENED: 'References ready deep link opened',
  CITATION_REQUEST_CREATED: 'Citation request created',
  CITATION_REQUEST_CREATE_FAILED: 'Citation request create failed',
  CITATION_REQUEST_REVIEW_OPENED: 'Citation request review opened',
  CITATION_REQUEST_APPROVED: 'Citation request approved',
  CITATION_REQUEST_APPROVE_FAILED: 'Citation request approve failed',
  CITATION_REQUEST_CANCELLED: 'Citation request cancelled',
  STATEMENT_CITATION_TRIAGE_DISMISSED: 'Statement citation triage dismissed',
  STATEMENT_CITATION_TRIAGE_OPENED: 'Statement citation triage opened',
  STATEMENT_CITATION_TRIAGE_PERSIST_FAILED: 'Statement citation triage persist failed',
  STATEMENT_CITATION_TRIAGE_SAVED: 'Statement citation triage saved',
  STATEMENT_REFERENCE_HELP_REQUESTED: 'Statement reference help requested',
  STATEMENT_REFERENCE_HELP_CANCELED: 'Statement reference help canceled',
  PROVIDE_REFERENCES_OPENED: 'Provide references opened',
  PROVIDE_REFERENCES_PUBLISHED: 'Provide references published',
  PROVIDE_REFERENCES_PUBLISH_FAILED: 'Provide references publish failed',
  PROVIDE_REFERENCES_DISMISSED: 'Provide references dismissed',
  REMOVE_ATTACHMENT_FILE_CANCELED: 'Remove attachment file canceled',
  REMOVE_ATTACHMENT_FILE_COMPLETED: 'Remove attachment file completed',
  REMOVE_ATTACHMENT_FILE_FAILED: 'Remove attachment file failed',
  REMOVE_ATTACHMENT_FILE_STARTED: 'Remove attachment file started',
  SAVED_POSTS_TAB_SELECTED: 'Saved posts tab selected',
  SEARCH_FAILED: 'Search failed',
  SEARCH_SUBMITTED: 'Search submitted',
  SEND_INQUIRY_CLICKED: 'Send inquiry clicked',
  SHOW_PASSWORD_TOGGLED: 'Show password toggled',
  SIGNUP_BANNER_VIEWED: 'Signup banner viewed',
  SIGNUP_EMAIL_PREFILLED: 'Signup email prefilled',
  SMART_LAUNCH_FAILED: 'SMART launch failed',
  SMS_NOTIFICATIONS_ENABLED: 'SMS notifications enabled',
  SMS_PROMPT_BANNER_DISMISSED: 'SMS prompt banner dismissed',
  SMS_PROMPT_BANNER_VIEWED: 'SMS prompt banner viewed',
  SMS_PROMPT_ENABLED: 'SMS prompt enabled',
  SMS_PROMPT_OPTED_OUT: 'SMS prompt opted out',
  SMS_PROMPT_SHOWN: 'SMS prompt shown',
  SMS_PROMPT_SKIPPED: 'SMS prompt skipped',
  SUPPORT_CONTACT_STARTED: 'Support contact started',
  SUPPORT_FORWARD_INQUIRY_OPENED: 'Support forward inquiry opened',
  SUPPORT_MESSAGE_FAILED: 'Support message failed',
  SUPPORT_MESSAGE_SENT: 'Support message sent',
  SUPPORT_MODAL_OPENED: 'Support modal opened',
  SUPPORT_REQUEST_FAILED: 'Support Request Failed',
  SUPPORT_REQUEST_SENT: 'Support Request Sent',
  SUPPORT_SURVEY_OPENED: 'Support survey opened',
  SUPPORT_THREAD_OPENED: 'Support thread opened',
  SUPPORT_THREAD_STARTED: 'Support thread started',
  SURGEON_ATTACHMENT_REMINDER_ATTACH_CLICKED: 'Surgeon attachment reminder attach clicked',
  SURGEON_ATTACHMENT_REMINDER_SHOWN: 'Surgeon attachment reminder shown',
  SURGEON_ATTACHMENT_REMINDER_SUBMIT_CLICKED: 'Surgeon attachment reminder submit clicked',
  SURVEY_AUTH_GATE_VIEWED: 'Survey auth gate viewed',
  SURVEY_PAGE_VIEWED: 'Survey page viewed',
  TERMS_CLICKED: 'Terms clicked',
  TOKEN_REVOKED_DETECTED: 'Token revoked detected',
  TOKEN_REVOKED_SESSION_CLEARED: 'Token revoked session cleared',
  UPDATE_PASSWORD_PAGE_SKIPPED: 'Update Password Page Skipped',
  UPDATE_PASSWORD_PAGE_SUBMITTED: 'Update Password Page Submitted',
  UPDATE_PASSWORD_PAGE_VIEWED: 'Update Password Page Viewed',
  USE_TITLE_TOGGLED: 'Use title toggled',
} as const;

export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/**
 * Custom enrichment keys injected by analytics plugins.
 *
 * These keys are attached to Segment payload context/properties and should not be
 * repurposed for feature-level tracking props.
 */
export const ANALYTICS_CUSTOM_KEYS = {
  APP_GENERATION_IDENTIFIER: 'appGenerationIdentifier',
  IS_TEST_USER_EVENT: 'isTestUserEvent',
} as const;

/**
 * Check if analytics tracking should be disabled.
 * Uses the unified storage key for consistency across platforms.
 *
 * Web only — returns false on mobile and native platforms.
 * For native, use the isAnalyticsDisabled function in analytics.ts
 *
 * Returns true if analytics should be disabled, false otherwise.
 */
export const disableAnalytics = (): boolean => {
  if (!isWeb) {
    return false;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const value = window.localStorage.getItem(STORAGE_KEYS.ANALYTICS_SEGMENT_ENABLED);

  return value === 'false';
};

export type AnalyticCustomKeys = (typeof ANALYTICS_CUSTOM_KEYS)[keyof typeof ANALYTICS_CUSTOM_KEYS];
